import React, { useState, useEffect } from 'react';
import { journalService } from '../services/api';
import { Card } from './ui';

const OnThisDay = ({ userId, currentDay }) => {
  const [retrospectives, setRetrospectives] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRetrospective = async () => {
      if (!userId || currentDay < 30) {
        setLoading(false);
        return;
      }

      try {
        const response = await journalService.getRetrospective(userId, currentDay);
        setRetrospectives(response.data.data.retrospectives);
      } catch (err) {
        console.error('Failed to load retrospective data:', err);
        // Don't show error - just fail silently if data isn't available
      } finally {
        setLoading(false);
      }
    };

    loadRetrospective();
  }, [userId, currentDay]);

  if (loading) {
    return null; // Silent load
  }

  if (!retrospectives || Object.keys(retrospectives).length === 0) {
    return null; // No retrospective data to show
  }

  const moodColor = (mood) => {
    if (mood <= 2) return 'text-red-600';
    if (mood <= 4) return 'text-orange-600';
    if (mood <= 6) return 'text-yellow-600';
    if (mood <= 8) return 'text-blue-600';
    return 'text-green-600';
  };

  const cravingColor = (craving) => {
    if (craving >= 8) return 'text-red-600';
    if (craving >= 6) return 'text-orange-600';
    if (craving >= 4) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getMoodLabel = (mood) => {
    if (mood === null || mood === undefined) return 'No mood recorded';
    if (mood <= 2) return 'Very low';
    if (mood <= 4) return 'Low';
    if (mood <= 6) return 'Moderate';
    if (mood <= 8) return 'Good';
    return 'Excellent';
  };

  const getCravingLabel = (craving) => {
    if (craving === null || craving === undefined) return 'No challenge level recorded';
    if (craving >= 8) return 'Very strong';
    if (craving >= 6) return 'Strong';
    if (craving >= 4) return 'Moderate';
    return 'Mild';
  };

  return (
    <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 rounded-lg p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-gray-900">
          ✨ On This Day in Your Mental Wellness Journey
        </h3>
        <p className="text-sm text-gray-600 mt-1">
          See how far you've come. Browse your journal from 30, 60, and 90 days ago.
        </p>
      </div>

      <div className="space-y-4">
        {Object.entries(retrospectives)
          .sort(([, a], [, b]) => a.day - b.day)
          .map(([key, entry]) => (
            <Card key={key} className="bg-white border border-blue-200">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-600 mb-1">
                    {entry.daysAgo} days ago • Day {entry.day}
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-2">
                    {new Date(entry.date).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>

                  <div className="text-sm text-gray-700 leading-relaxed mb-3 italic">
                    "{entry.contentPreview}"
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm mt-3">
                    {entry.tracking?.moodScale !== undefined && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">Mood:</span>
                        <span className={`font-semibold ${moodColor(entry.tracking.moodScale)}`}>
                          {entry.tracking.moodScale}/10
                        </span>
                        <span className="text-gray-500">
                          ({getMoodLabel(entry.tracking.moodScale)})
                        </span>
                      </div>
                    )}
                    {entry.tracking?.cravingScale !== undefined && (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-600">Challenge:</span>
                        <span className={`font-semibold ${cravingColor(entry.tracking.cravingScale)}`}>
                          {entry.tracking.cravingScale}/10
                        </span>
                        <span className="text-gray-500">
                          ({getCravingLabel(entry.tracking.cravingScale)})
                        </span>
                      </div>
                    )}
                  </div>

                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {entry.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div className="text-center text-xs text-gray-600 whitespace-nowrap">
                  <div className="font-semibold text-lg text-blue-600 mb-1">
                    +{entry.daysAgo}
                  </div>
                  <div>days of</div>
                  <div>progress</div>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100">
                <div className="bg-blue-50 text-blue-800 text-xs p-2 rounded flex items-start space-x-2">
                  <span className="mt-0.5">💭</span>
                  <p>
                    {entry.daysAgo === 30 && "A month ago, look at where you were then. Progress isn't always obvious in the moment."}
                    {entry.daysAgo === 60 && "Two months back. Notice the patterns, the growth, the moments of strength."}
                    {entry.daysAgo === 90 && "Three months ago. That's a full quarter of mental wellness commitment. That matters."}
                  </p>
                </div>
              </div>
            </Card>
          ))}
      </div>

      <div className="mt-4 p-3 bg-white border border-blue-200 rounded text-sm text-gray-700">
        <p className="font-semibold text-gray-900 mb-1">Why we show you this:</p>
        <p>
          Mental wellness is about direction, not perfection. Looking back helps you see patterns you might miss day-to-day. 
          You're building something real, and the evidence is in your own words.
        </p>
      </div>
    </div>
  );
};

export default OnThisDay;
